import type { components } from "../../api/generated-schema";
import { apiRequest, createRequestId } from "../../api/httpClient";

export type AdministrativeAreaTreeNode = components["schemas"]["AdministrativeAreaTreeNode"];
export type AdministrativeAreaDetail = components["schemas"]["AdministrativeArea"];
export type AdministrativeAreaVersionView = components["schemas"]["AdministrativeAreaVersion"];
export type AdministrativeAreaCommandResult = components["schemas"]["AdministrativeAreaCommandResult"];
export type AdministrativeAreaMergeResult = components["schemas"]["AdministrativeAreaMergeResult"];
export type CreateAdministrativeAreaRequest = components["schemas"]["CreateAdministrativeAreaRequest"];
export type OpenAdministrativeAreaRequest = components["schemas"]["OpenAdministrativeAreaRequest"];
export type CloseAdministrativeAreaRequest = components["schemas"]["CloseAdministrativeAreaRequest"];
export type RenameAdministrativeAreaRequest = components["schemas"]["RenameAdministrativeAreaRequest"];
export type TransferAdministrativeAreaRequest = components["schemas"]["TransferAdministrativeAreaRequest"];
export type MergeAdministrativeAreaRequest = components["schemas"]["MergeAdministrativeAreaRequest"];

const BASE_PATH = "/admin/administrative-areas";

function asOfQuery(asOf?: string): string {
  return asOf ? `?${new URLSearchParams({ asOf }).toString()}` : "";
}

function commandHeaders(idempotencyKey: string): Record<string, string> {
  return { "Idempotency-Key": idempotencyKey, "X-Request-Id": createRequestId() };
}

function signalInit(signal?: AbortSignal): RequestInit {
  return signal ? { signal } : {};
}

function postCommand<T>(path: string, body: unknown, idempotencyKey: string): Promise<T> {
  return apiRequest<T>(path, {
    method: "POST",
    headers: commandHeaders(idempotencyKey),
    body: JSON.stringify(body),
  });
}

export function getAdministrativeAreaTree(
  asOf?: string,
  signal?: AbortSignal,
): Promise<AdministrativeAreaTreeNode[]> {
  return apiRequest<AdministrativeAreaTreeNode[]>(
    `${BASE_PATH}${asOfQuery(asOf)}`,
    signalInit(signal),
  );
}

export function getAdministrativeAreaDetail(
  administrativeAreaId: string,
  asOf?: string,
  signal?: AbortSignal,
): Promise<AdministrativeAreaDetail> {
  return apiRequest<AdministrativeAreaDetail>(
    `${BASE_PATH}/${encodeURIComponent(administrativeAreaId)}${asOfQuery(asOf)}`,
    signalInit(signal),
  );
}

export function getAdministrativeAreaHistory(
  administrativeAreaId: string,
  signal?: AbortSignal,
): Promise<AdministrativeAreaVersionView[]> {
  return apiRequest<AdministrativeAreaVersionView[]>(
    `${BASE_PATH}/${encodeURIComponent(administrativeAreaId)}/history`,
    signalInit(signal),
  );
}

export function createAdministrativeArea(
  body: CreateAdministrativeAreaRequest,
  idempotencyKey: string,
): Promise<AdministrativeAreaCommandResult> {
  return postCommand<AdministrativeAreaCommandResult>(BASE_PATH, body, idempotencyKey);
}

export function openAdministrativeArea(
  administrativeAreaId: string,
  body: OpenAdministrativeAreaRequest,
  idempotencyKey: string,
): Promise<AdministrativeAreaCommandResult> {
  return postCommand<AdministrativeAreaCommandResult>(
    `${BASE_PATH}/${encodeURIComponent(administrativeAreaId)}/open`,
    body,
    idempotencyKey,
  );
}

export function closeAdministrativeArea(
  administrativeAreaId: string,
  body: CloseAdministrativeAreaRequest,
  idempotencyKey: string,
): Promise<AdministrativeAreaCommandResult> {
  return postCommand<AdministrativeAreaCommandResult>(
    `${BASE_PATH}/${encodeURIComponent(administrativeAreaId)}/close`,
    body,
    idempotencyKey,
  );
}

export function renameAdministrativeArea(
  administrativeAreaId: string,
  body: RenameAdministrativeAreaRequest,
  idempotencyKey: string,
): Promise<AdministrativeAreaCommandResult> {
  return postCommand<AdministrativeAreaCommandResult>(
    `${BASE_PATH}/${encodeURIComponent(administrativeAreaId)}/rename`,
    body,
    idempotencyKey,
  );
}

export function transferAdministrativeArea(
  administrativeAreaId: string,
  body: TransferAdministrativeAreaRequest,
  idempotencyKey: string,
): Promise<AdministrativeAreaCommandResult> {
  return postCommand<AdministrativeAreaCommandResult>(
    `${BASE_PATH}/${encodeURIComponent(administrativeAreaId)}/transfer`,
    body,
    idempotencyKey,
  );
}

export function mergeAdministrativeArea(
  survivorId: string,
  body: MergeAdministrativeAreaRequest,
  idempotencyKey: string,
): Promise<AdministrativeAreaMergeResult> {
  return postCommand<AdministrativeAreaMergeResult>(
    `${BASE_PATH}/${encodeURIComponent(survivorId)}/merge`,
    body,
    idempotencyKey,
  );
}
